import React from "react";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import { NavIcon } from "./NavIcons";

// Shared "front desk facts" strip — reused on the manager home, the manager
// reports page, and the owner (org) dashboard. Uses the same large-icon KPI
// cards as the Home performance section. Feed it a snapshot in the shape
// { rooms, period, owed, events } returned by /reservations/snapshot (the owner
// page builds the same shape from its org aggregate).
//
// Pass `compact` for a denser layout (smaller icons/tiles/fonts) — used on the
// manager home so the strip doesn't dominate the page.
const S = {
  en: {
    title: "Front desk", sc_mtd: "month to date", sc_org: "all branches", sc_range: "selected range",
    rooms: "Rooms & occupancy", revenue: "Revenue", owed: "Money owed", events: "Events",
    occNow: "Occupancy now", inHouse: "In-house", arrivals: "Arrivals due", departures: "Departures due",
    roomRev: "Room revenue", adr: "ADR", revpar: "RevPAR", occPeriod: "Occupancy",
    inHouseBal: "In-house balance", deposits: "Deposits held", company: "Company / city ledger",
    upcoming: "Upcoming", evRev: "Events revenue", evBal: "Events balance",
  },
  am: {
    title: "የፊት ዴስክ", sc_mtd: "ከወር መጀመሪያ", sc_org: "ሁሉም ቅርንጫፎች", sc_range: "የተመረጠ ክልል",
    rooms: "ክፍሎች እና ሙላት", revenue: "ገቢ", owed: "የሚከፈል ገንዘብ", events: "ዝግጅቶች",
    occNow: "አሁን ሙላት", inHouse: "በውስጥ", arrivals: "የሚገቡ", departures: "የሚወጡ",
    roomRev: "የክፍል ገቢ", adr: "አማካይ ዋጋ", revpar: "RevPAR", occPeriod: "ሙላት",
    inHouseBal: "የውስጥ ቀሪ", deposits: "ተይዞ ያለ ቅድሚያ", company: "ድርጅት / ሌጀር",
    upcoming: "መጪ", evRev: "የዝግጅት ገቢ", evBal: "የዝግጅት ቀሪ",
  },
  om: {
    title: "Fuula duraa", sc_mtd: "ji'a kana", sc_org: "damee hunda", sc_range: "daangaa filatame",
    rooms: "Kutaalee & guuttii", revenue: "Galii", owed: "Maallaqa kaffalamu", events: "Sagantaalee",
    occNow: "Guuttii ammaa", inHouse: "Keessa jiru", arrivals: "Kan seenan", departures: "Kan ba'an",
    roomRev: "Galii kutaa", adr: "Gatii giddu", revpar: "RevPAR", occPeriod: "Guuttii",
    inHouseBal: "Hafe keessaa", deposits: "Kaffaltii duraa", company: "Dhaabbata / leejara",
    upcoming: "Dhufu", evRev: "Galii sagantaa", evBal: "Hafe sagantaa",
  },
};

// tint = soft background, accent = icon + value colour (matches Home cards).
const TEAL = { tint: "#E1F5EE", accent: "#0F6E56" };
const BLUE = { tint: "#E6F1FB", accent: "#0C447C" };
const GREEN = { tint: "#EAF3DE", accent: "#27500A" };
const AMBER = { tint: "#FAEEDA", accent: "#854F0B" };
const CORAL = { tint: "#FAECE7", accent: "#712B13" };
const PURPLE = { tint: "#EEEDFE", accent: "#3C3489" };

function Metric({ icon, label, value, c, compact }) {
  const pad = compact ? "8px 10px" : "14px 16px";
  const icon_sz = compact ? 26 : 46;
  const val_sz = compact ? 16 : 22;
  const lab_sz = compact ? 12 : 13.5;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? 9 : 14, background: c.tint, borderRadius: compact ? 11 : 14, padding: pad, boxShadow: "0 1px 3px rgba(6,59,72,0.05)" }}>
      <div style={{ flex: "0 0 auto", lineHeight: 0 }}><NavIcon name={icon} size={icon_sz} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: lab_sz, color: "#5f6b78", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: val_sz, fontWeight: 700, color: c.accent, lineHeight: 1.2, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      </div>
    </div>
  );
}

export default function FrontDeskFacts({ snapshot, cur = "ETB", scope, compact = false }) {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  if (!snapshot) return null;
  const rooms = snapshot.rooms || {};
  const period = snapshot.period || {};
  const owed = snapshot.owed || {};
  const events = snapshot.events || {};
  const m = (n) => formatPrice(n || 0, cur);
  const pct = (v) => (v == null ? "—" : `${v}%`);

  const GRP = { fontSize: compact ? 10.5 : 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#0F6E56", margin: compact ? "2px 2px 0" : "6px 2px 0" };
  const grid = { display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${compact ? 140 : 200}px, 1fr))`, gap: compact ? 8 : 12 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 6 : 10 }}>
      {!compact && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: "#063b48" }}>{t("title")}</span>
          {scope && <span style={{ fontSize: 12, color: "#8a949b" }}>· {t("sc_" + scope)}</span>}
        </div>
      )}

      <div style={GRP}>{t("rooms")}</div>
      <div style={grid}>
        <Metric icon="bed" label={t("occNow")} value={pct(rooms.occupancy_today_pct)} c={TEAL} compact={compact} />
        <Metric icon="users" label={t("inHouse")} value={rooms.in_house || 0} c={BLUE} compact={compact} />
        <Metric icon="calendar" label={t("arrivals")} value={rooms.arrivals_due || 0} c={GREEN} compact={compact} />
        <Metric icon="key" label={t("departures")} value={rooms.departures_due || 0} c={CORAL} compact={compact} />
      </div>

      <div style={GRP}>{t("revenue")}</div>
      <div style={grid}>
        <Metric icon="cash" label={t("roomRev")} value={m(period.room_revenue)} c={GREEN} compact={compact} />
        <Metric icon="bar-chart" label={t("adr")} value={m(period.adr)} c={BLUE} compact={compact} />
        <Metric icon="pie" label={t("revpar")} value={m(period.revpar)} c={AMBER} compact={compact} />
        <Metric icon="bed" label={t("occPeriod")} value={pct(period.occupancy_pct)} c={TEAL} compact={compact} />
      </div>
      {!compact && (
        <div style={{ fontSize: 12, color: "#8a949b", paddingLeft: 2 }}>
          {t("adr")} {m(period.adr)} × {t("occPeriod")} {pct(period.occupancy_pct)} = {t("revpar")} {m(period.revpar)}
        </div>
      )}

      <div style={GRP}>{t("owed")}</div>
      <div style={grid}>
        <Metric icon="receipt" label={t("inHouseBal")} value={m(owed.in_house_balance)} c={AMBER} compact={compact} />
        <Metric icon="cash" label={t("deposits")} value={m(owed.deposits_held)} c={TEAL} compact={compact} />
        <Metric icon="building" label={t("company")} value={m(owed.company_owed)} c={CORAL} compact={compact} />
      </div>

      <div style={GRP}>{t("events")}</div>
      <div style={grid}>
        <Metric icon="calendar" label={t("upcoming")} value={events.upcoming || 0} c={PURPLE} compact={compact} />
        <Metric icon="utensils" label={t("evRev")} value={m(events.revenue)} c={GREEN} compact={compact} />
        <Metric icon="receipt" label={t("evBal")} value={m(events.balance)} c={AMBER} compact={compact} />
      </div>
    </div>
  );
}
